import React, { RefObject, useCallback, useEffect, useMemo, useState } from 'react';
import * as mobxReact from 'mobx-react';
import './search-filter.scss';
import { ReportConfigurationViewModel } from '../../../_reports/report-configuration/report-configuration.viewmodel';
import { IReportOperator, IReportOperatorItem } from '../../../../../../api/reports.dao';
import OECheckbox from '../../../../../global/oe-checkbox/oe-checkbox';
import Text from '../../../../../global/text/text';

interface IProps {
    field: string;
    placeholder?: string;
    configurationVm: ReportConfigurationViewModel;
    ContentHeader: JSX.Element;
    headerHeight: string;
    headerRef: RefObject<HTMLDivElement>;
    defaultOperator?: IReportOperator;
}

const SearchFilter = mobxReact.observer((props: IProps) => {
    const {
        field,
        placeholder,
        configurationVm,
        ContentHeader,
        headerHeight,
        headerRef,
        defaultOperator
    } = props;

    const operators: IReportOperatorItem[] = [{
        label: 'Includes',
        value: 'CONTAINS'
    }, {
        label: 'Excludes',
        value: 'NOT_CONTAINS'
    }];

    const { applyFilter, clearFilter, getAppliedFilter } = configurationVm;
    const appliedFilter = getAppliedFilter(field);
    const [inputValue, setInputValue] = useState('');
    const [operatorValue, setOperatorValue] = useState(defaultOperator || operators[0].value);
    const [allTerms, setAllTerms] = useState([]);

    useEffect(() => {
       if (appliedFilter) {
            setOperatorValue(appliedFilter.operator);
            setInputValue('');
            setAllTerms(appliedFilter.values);
        }

    }, [field]);

    const isValueSelected = (value: string): boolean => {
        return appliedFilter?.values.map(el => el.toLowerCase()).includes(value.toLowerCase()) || false;
    };

    const filterCommonProps = {
        field: field,
        customField: false,
        operator: operatorValue,
    };

    const selectOne = (value: string) => {
        if (isValueSelected(value)) {
            const newValues = appliedFilter.values.filter(el => el.toLowerCase() !== value.toLowerCase());
            if (newValues.length) {
                applyFilter({...filterCommonProps, values: newValues});
            } else {
                clearFilter(field);
            }
        } else {
            if (!allTerms.map(el => el.toLowerCase()).includes(value.toLowerCase())) {
                setAllTerms(prev => [...prev, value]);
            }
            applyFilter({...filterCommonProps, values: [...(appliedFilter?.values || []), value].filter(Boolean)});
        }
    };

    const handleChangeOperator = (value: IReportOperator) => {
        setOperatorValue(value);
        if (appliedFilter) applyFilter({...filterCommonProps, operator: value, values: appliedFilter.values});
    };

    const getSearchResults = useCallback(() => {
        return inputValue.trim().length ? [{ label: `"${inputValue}"`, value: inputValue }] : [];
    }, [inputValue]);

    const searchResults = useMemo(() => getSearchResults(), [inputValue]);

    const selectedTerms = appliedFilter?.values || [];
    const selectedTermsLabel = ` Selected Term${selectedTerms.length === 1 ? '' : 's'}`;

    return (
        <div className='search-filter'>
            <div className='search-filter__header' ref={headerRef}>
                {ContentHeader}
                <div className='search-filter__header-content'>
                    <select
                        className='search-filter__operator-chooser oe-form-field__control'
                        value={operatorValue}
                        onChange={e => handleChangeOperator(e.target.value as IReportOperator)}
                    >
                        { operators.map(operator =>
                            <option key={operator.value} value={operator.value}>{operator.label}</option>
                        )}
                    </select>
                    <input
                        className='search-filter__search-input oe-form-field__control'
                        placeholder={placeholder || 'Search'}
                        value={inputValue}
                        onChange={e => setInputValue(e.target.value)}
                    />
                </div>
            </div>
            <div className='search-filter__content' style={{ height: headerHeight }}>
                <div className='search-filter__search-results search-filter__section'>
                    { !!searchResults.length && <div className='search-filter__content-label'>
                        Select to add to filter
                    </div> }
                    <div className='search-filter__items'>
                        { searchResults.map(item => (
                            <OECheckbox
                                key={item.value}
                                checked={isValueSelected(item.value)}
                                onChange={() => selectOne(item.value)}
                                renderLabel={<Text message={item.label || item.value} highlightValue={inputValue} withHighlight />}
                                classNames='oe-form-field__checkbox search-filter__checkbox'
                            />
                        ))}
                    </div>
                </div>
                <div className='search-filter__selected-terms search-filter__section'>
                    <div className='search-filter__content-label'>
                        <span className='search-filter__content-label-count'>{selectedTerms.length}</span>
                        {selectedTermsLabel}
                    </div>
                    <div className='search-filter__items'>
                        { allTerms?.map(value => (
                            <OECheckbox
                                key={value}
                                checked={isValueSelected(value)}
                                onChange={() => selectOne(value)}
                                renderLabel={<Text message={value} highlightValue={inputValue} withHighlight />}
                                classNames='oe-form-field__checkbox search-filter__checkbox'
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
});

export default SearchFilter;
